import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Breadcrumb, Spinner } from '../../../shared/components';
import { useAuth } from '../../auth/context/AuthContext';
import { PermissionCodes } from '../../auth/permissions';
import { useToast } from '../../notifications';
import {
  createApiTool,
  createApiToolConnection,
  deleteApiTool,
  deleteApiToolConnection,
  disconnectApiToolOAuth,
  importSpecFromUrlToConnection,
  importSpecToConnection,
  importSpecUploadToConnection,
  listApiToolConnections,
  listApiTools,
  previewApiTool,
  previewImportOpenApiSpec,
  previewImportOpenApiSpecFromUrl,
  previewImportOpenApiSpecUpload,
  startApiToolOAuth,
  testApiToolConnection,
  updateApiTool,
  updateApiToolConnection,
} from '../services/apiToolsService';
import type {
  ApiToolConnection,
  ApiToolDefinition,
  ApiToolImportedOperation,
  ApiToolParameterBinding,
  ImportOpenApiSpecResult,
  SpecImportMode,
} from '../types/ApiTools';
import {
  applySuggestedAuthToForm,
  buildCreateConnectionRequest,
  buildUpdateConnectionRequest,
  createEmptyDefaultHeader,
  createEmptyParameterBinding,
  emptyConnectionForm,
  emptyManualToolForm,
  normalizeParameterBindingsForSave,
  suggestKernelFunctionName,
  validateConnectionAuth,
} from '../utils/apiToolsFormUtils';
import {
  getAuthPreviewWarning,
  parseApiToolPreviewResult,
} from '../utils/apiToolPreviewUtils';
import { listAiToolPlugins } from '../../ai-tool-plugins/services/toolPluginService';
import type { AiToolPluginDefinition } from '../../ai-tool-plugins/types/ToolPlugin';
import type { AiPromptUseCaseId } from '../../admin/types/AiPromptConfig';
import { ToolUseCaseAssignment } from '../../tools/components/ToolUseCaseAssignment';
import { DEFAULT_ASSIGNED_USE_CASE_IDS } from '../../tools/constants/assignableUseCases';
import { getToolUseCaseAssignments } from '../../tools/services/toolUseCaseAssignmentService';
import styles from './ApiToolsPage.module.css';

const CONNECTION_AUTH_SECTION_ID = 'connection-authentication';
const PARAMETER_LOCATIONS = ['path', 'query', 'header', 'body'] as const;

type FormFieldProps = {
  id?: string;
  label: React.ReactNode;
  hint?: string;
  fullWidth?: boolean;
  children: React.ReactNode;
};

function FormField({ id, label, hint, fullWidth, children }: FormFieldProps) {
  return (
    <label className={fullWidth ? styles.formFieldFull : undefined} htmlFor={id}>
      <span className={styles.fieldLabel}>{label}</span>
      {children}
      {hint ? <span className={styles.fieldHint}>{hint}</span> : null}
    </label>
  );
}

type ParameterBindingsEditorProps = {
  bindings: ApiToolParameterBinding[];
  disabled?: boolean;
  onChange: (bindings: ApiToolParameterBinding[]) => void;
};

function ParameterBindingsEditor({ bindings, disabled, onChange }: ParameterBindingsEditorProps) {
  const { t } = useTranslation('apiTools');

  const updateBinding = (index: number, patch: Partial<ApiToolParameterBinding>) => {
    onChange(bindings.map((binding, bindingIndex) => (
      bindingIndex === index ? { ...binding, ...patch } : binding
    )));
  };

  return (
    <div className={styles.formFieldFull}>
      <strong>{t('parameterBindings')}</strong>
      <p className={styles.fieldHint}>{t('parameterBindingsHint')}</p>
      <div className={styles.parameterBindingsEditor}>
        {bindings.map((binding, index) => (
          <div key={`parameter-binding-${index}`} className={styles.parameterBindingCard}>
            <div className={styles.parameterBindingRow}>
              <input
                className="text-input"
                value={binding.name}
                disabled={disabled}
                onChange={(event) => updateBinding(index, { name: event.target.value })}
                placeholder={t('paramName')}
                autoComplete="off"
                spellCheck={false}
              />
              <select
                className="text-input"
                value={binding.location}
                disabled={disabled}
                onChange={(event) => updateBinding(index, { location: event.target.value })}
              >
                {PARAMETER_LOCATIONS.map((location) => (
                  <option key={location} value={location}>
                    {t(`paramLocation_${location}`)}
                  </option>
                ))}
              </select>
              <input
                className="text-input"
                value={binding.sourceName}
                disabled={disabled}
                onChange={(event) => updateBinding(index, { sourceName: event.target.value })}
                placeholder={t('paramSourceNamePlaceholder')}
                autoComplete="off"
                spellCheck={false}
              />
              <label className={styles.parameterRequiredToggle}>
                <input
                  type="checkbox"
                  checked={binding.isRequired}
                  disabled={disabled}
                  onChange={(event) => updateBinding(index, { isRequired: event.target.checked })}
                />
                {t('paramRequired')}
              </label>
            </div>
            <input
              className="text-input"
              value={binding.description}
              disabled={disabled}
              onChange={(event) => updateBinding(index, { description: event.target.value })}
              placeholder={t('paramDescription')}
            />
            <div className={styles.parameterBindingMetaRow}>
              <input
                className="text-input"
                value={binding.defaultValue}
                disabled={disabled}
                onChange={(event) => updateBinding(index, { defaultValue: event.target.value })}
                placeholder={t('paramDefaultValue')}
                autoComplete="off"
              />
              <input
                className="text-input"
                value={binding.example}
                disabled={disabled}
                onChange={(event) => updateBinding(index, { example: event.target.value })}
                placeholder={t('paramExample')}
                autoComplete="off"
              />
              <button
                type="button"
                className="btn btn-secondary"
                disabled={disabled}
                onClick={() => onChange(bindings.filter((_, bindingIndex) => bindingIndex !== index))}
              >
                {t('removeParameter')}
              </button>
            </div>
          </div>
        ))}
        {bindings.length === 0 ? (
          <p className={styles.fieldHint}>{t('noParameters')}</p>
        ) : null}
      </div>
      <div className={styles.actionsRow}>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={disabled}
          onClick={() => onChange([...bindings, createEmptyParameterBinding()])}
        >
          {t('addParameter')}
        </button>
      </div>
      <span className={styles.fieldHint}>{t('paramSourceNameHint')}</span>
    </div>
  );
}

type ManualToolFormState = typeof emptyManualToolForm;

type ManualToolCreatePanelProps = {
  form: ManualToolFormState;
  saving: boolean;
  connectionAuthReady: boolean;
  onChange: React.Dispatch<React.SetStateAction<ManualToolFormState>>;
  onSubmit: () => void;
  onScrollToAuth: () => void;
};

function ManualToolCreatePanel({
  form,
  saving,
  connectionAuthReady,
  onChange,
  onSubmit,
  onScrollToAuth,
}: ManualToolCreatePanelProps) {
  const { t } = useTranslation('apiTools');

  return (
    <div className={`company-form-grid ${styles.toolEditFormGrid}`}>
      <FormField id="manual-display-name" label={t('displayName')}>
        <input
          id="manual-display-name"
          className="text-input"
          value={form.displayName}
          onChange={(event) => onChange((current) => {
            const displayName = event.target.value;
            const shouldSuggestKernelName = !current.kernelFunctionName.trim()
              || current.kernelFunctionName === suggestKernelFunctionName(current.displayName);

            return {
              ...current,
              displayName,
              kernelFunctionName: shouldSuggestKernelName
                ? suggestKernelFunctionName(displayName)
                : current.kernelFunctionName,
            };
          })}
          placeholder={t('displayNamePlaceholder')}
        />
      </FormField>
      <FormField id="manual-tool-id" label={t('toolId')} hint={t('toolIdHint')}>
        <input
          id="manual-tool-id"
          className="text-input"
          value={form.toolId}
          onChange={(event) => onChange((current) => ({
            ...current,
            toolId: event.target.value,
          }))}
          placeholder={t('toolIdPlaceholder')}
          autoComplete="off"
          spellCheck={false}
        />
      </FormField>
      <FormField id="manual-kernel-name" label={t('kernelFunctionName')} hint={t('kernelFunctionNameHint')}>
        <input
          id="manual-kernel-name"
          className="text-input"
          value={form.kernelFunctionName}
          onChange={(event) => onChange((current) => ({
            ...current,
            kernelFunctionName: event.target.value,
          }))}
          placeholder={t('kernelFunctionNamePlaceholder')}
          autoComplete="off"
          spellCheck={false}
        />
      </FormField>
      <FormField id="manual-http-method" label={t('httpMethod')}>
        <select
          id="manual-http-method"
          className="text-input"
          value={form.httpMethod}
          onChange={(event) => onChange((current) => ({
            ...current,
            httpMethod: event.target.value,
          }))}
        >
          {['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'].map((method) => (
            <option key={method} value={method}>{method}</option>
          ))}
        </select>
      </FormField>
      <FormField id="manual-path-template" label={t('pathTemplate')}>
        <input
          id="manual-path-template"
          className={`text-input ${styles.monoField}`}
          value={form.pathTemplate}
          onChange={(event) => onChange((current) => ({
            ...current,
            pathTemplate: event.target.value,
          }))}
          placeholder="/customers/{customerId}"
        />
      </FormField>
      <FormField id="manual-description" label={t('description')} fullWidth>
        <textarea
          id="manual-description"
          className="text-input"
          rows={3}
          value={form.description}
          onChange={(event) => onChange((current) => ({
            ...current,
            description: event.target.value,
          }))}
          placeholder={t('descriptionPlaceholder')}
        />
      </FormField>

      <ParameterBindingsEditor
        bindings={form.parameterBindings}
        disabled={saving}
        onChange={(parameterBindings) => onChange((current) => ({
          ...current,
          parameterBindings,
        }))}
      />

      <FormField id="manual-request-content-type" label={t('requestContentType')} hint={t('requestContentTypeHint')}>
        <select
          id="manual-request-content-type"
          className="text-input"
          value={form.requestContentType}
          disabled={saving}
          onChange={(event) => onChange((current) => ({
            ...current,
            requestContentType: event.target.value,
          }))}
        >
          <option value="application/json">{t('requestContentTypeJson')}</option>
          <option value="application/x-www-form-urlencoded">{t('requestContentTypeForm')}</option>
        </select>
      </FormField>

      <FormField id="manual-response-mode" label={t('responseMode')}>
        <select
          id="manual-response-mode"
          className="text-input"
          value={form.responseMode}
          disabled={saving}
          onChange={(event) => onChange((current) => ({
            ...current,
            responseMode: event.target.value,
          }))}
        >
          <option value="full">{t('responseModeFull')}</option>
          <option value="jsonPath">{t('responseModeJsonPath')}</option>
        </select>
      </FormField>
      {form.responseMode === 'jsonPath' ? (
        <FormField
          id="manual-response-json-path"
          label={t('responseJsonPath')}
          hint={t('responseJsonPathHint')}
          fullWidth
        >
          <input
            id="manual-response-json-path"
            className="text-input"
            value={form.responseJsonPath}
            disabled={saving}
            onChange={(event) => onChange((current) => ({
              ...current,
              responseJsonPath: event.target.value,
            }))}
            placeholder="data.items"
          />
        </FormField>
      ) : null}

      <label className={`${styles.publishedToggle} ${styles.formFieldFull} ${form.isPublished ? styles.publishedTogglePublished : ''}`}>
        <input
          type="checkbox"
          checked={form.isPublished}
          disabled={saving}
          onChange={(event) => onChange((current) => ({
            ...current,
            isPublished: event.target.checked,
          }))}
        />
        <span>
          <strong>{form.isPublished ? t('published') : t('draft')}</strong>
          <div style={{ fontSize: 12, color: '#64748b' }}>{t('publishedHelp')}</div>
        </span>
      </label>

      {form.isPublished && !connectionAuthReady ? (
        <div className={`${styles.authPreviewAlert} ${styles.formFieldFull}`} role="alert">
          <strong>{t('publishAuthRequiredTitle')}</strong>
          <p>{t('publishAuthRequiredBody')}</p>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={onScrollToAuth}
          >
            {t('authPreviewUpdateCredentials')}
          </button>
        </div>
      ) : null}

      <div className={styles.formFieldFull}>
        <ToolUseCaseAssignment
          selectedUseCaseIds={form.assignedUseCaseIds}
          disabled={saving}
          isPublished={form.isPublished}
          onChange={(assignedUseCaseIds) => onChange((current) => ({
            ...current,
            assignedUseCaseIds,
          }))}
        />
      </div>

      <div className={`${styles.actionsRow} ${styles.toolEditActions} ${styles.formFieldFull}`}>
        <button
          type="button"
          className="btn btn-primary"
          disabled={
            saving
            || !form.displayName.trim()
            || !form.kernelFunctionName.trim()
            || !form.pathTemplate.trim()
          }
          onClick={onSubmit}
        >
          {t('saveManualTool')}
        </button>
      </div>
    </div>
  );
}

const emptyToolForm = {
  toolId: '',
  connectionId: '',
  operationId: '',
  kernelFunctionName: '',
  displayName: '',
  description: '',
  httpMethod: 'GET',
  pathTemplate: '',
  isPublished: false,
  functionId: '',
  parameterBindings: [] as ApiToolDefinition['parameterBindings'],
  responseMode: 'full',
  responseJsonPath: '',
  assignedUseCaseIds: [] as AiPromptUseCaseId[],
  requestContentType: 'application/json',
};

type WorkspaceTab = 'setup' | 'import' | 'tools';

type ApiToolsPageProps = {
  embedded?: boolean;
};

export const ApiToolsPage: React.FC<ApiToolsPageProps> = ({ embedded = false }) => {
  const { t } = useTranslation(['apiTools', 'common', 'nav', 'tools', 'toolPlugins']);
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const canManage = hasPermission(PermissionCodes.apiToolsManage);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [connections, setConnections] = useState<ApiToolConnection[]>([]);
  const [tools, setTools] = useState<ApiToolDefinition[]>([]);
  const [toolPlugins, setToolPlugins] = useState<AiToolPluginDefinition[]>([]);
  const [selectedConnectionId, setSelectedConnectionId] = useState<string | null>(null);
  const [connectionForm, setConnectionForm] = useState(emptyConnectionForm);
  const [specContent, setSpecContent] = useState('');
  const [baseUrlOverride, setBaseUrlOverride] = useState('');
  const [specPreviewResult, setSpecPreviewResult] = useState<ImportOpenApiSpecResult | null>(null);
  const [testingConnection, setTestingConnection] = useState(false);
  const [connectionTestPath, setConnectionTestPath] = useState('/');
  const [connectionTestMethod, setConnectionTestMethod] = useState('GET');
  const [connectionTestResult, setConnectionTestResult] = useState('');
  const [previewingTool, setPreviewingTool] = useState(false);
  const [previewArgs, setPreviewArgs] = useState<Record<string, string>>({});
  const [toolPreviewResult, setToolPreviewResult] = useState('');
  const [selectedOperationIds, setSelectedOperationIds] = useState<string[]>([]);
  const [createOperationsAsPublished, setCreateOperationsAsPublished] = useState(false);
  const [createOperationAssignedUseCaseIds, setCreateOperationAssignedUseCaseIds] = useState<AiPromptUseCaseId[]>(
    [...DEFAULT_ASSIGNED_USE_CASE_IDS],
  );
  const [selectedConnectionToolIds, setSelectedConnectionToolIds] = useState<string[]>([]);
  const [bulkAssignedUseCaseIds, setBulkAssignedUseCaseIds] = useState<AiPromptUseCaseId[]>(
    [...DEFAULT_ASSIGNED_USE_CASE_IDS],
  );
  const [bulkPublishing, setBulkPublishing] = useState(false);
  const [selectedToolId, setSelectedToolId] = useState('');
  const [toolForm, setToolForm] = useState(emptyToolForm);
  const [specImportMode, setSpecImportMode] = useState<SpecImportMode>('paste');
  const [specUrl, setSpecUrl] = useState('');
  const [specFile, setSpecFile] = useState<File | null>(null);
  const [manualToolForm, setManualToolForm] = useState(emptyManualToolForm);
  const [showManualToolForm, setShowManualToolForm] = useState(false);
  const [connectingOAuth, setConnectingOAuth] = useState(false);
  const [workspaceTab, setWorkspaceTab] = useState<WorkspaceTab>('setup');

  const selectedConnection = useMemo(
    () => connections.find((connection) => connection.connectionId === selectedConnectionId) ?? null,
    [connections, selectedConnectionId],
  );

  const connectionTools = useMemo(
    () => (selectedConnectionId
      ? tools.filter((tool) => tool.connectionId === selectedConnectionId)
      : []),
    [selectedConnectionId, tools],
  );

  const pluginNamesByFunctionId = useMemo(() => {
    const map = new Map<string, string[]>();
    toolPlugins.forEach((plugin) => {
      plugin.memberFunctionIds.forEach((functionId) => {
        map.set(functionId, [...(map.get(functionId) ?? []), plugin.kernelPluginName]);
      });
    });
    return map;
  }, [toolPlugins]);

  const toolStatusCounts = useMemo(() => ({
    published: connectionTools.filter((tool) => tool.isPublished).length,
    draft: connectionTools.filter((tool) => !tool.isPublished).length,
  }), [connectionTools]);

  const existingOperationIds = useMemo(
    () => new Set(connectionTools.map((tool) => tool.operationId).filter(Boolean)),
    [connectionTools],
  );

  const authValueHint = useMemo(() => {
    switch (connectionForm.authType) {
      case 'apiKey':
        return t('authValueHintApiKey');
      case 'bearer':
        return t('authValueHintBearer');
      default:
        return undefined;
    }
  }, [connectionForm.authType, t]);

  const toolPreviewAuthWarning = useMemo(() => {
    if (!toolPreviewResult) {
      return null;
    }

    return getAuthPreviewWarning(
      parseApiToolPreviewResult(toolPreviewResult),
      selectedConnection?.authType ?? 'none',
      selectedConnection?.authConfigured ?? selectedConnection?.oauthConnected ?? false,
    );
  }, [toolPreviewResult, selectedConnection]);

  const isManualTool = !toolForm.operationId;

  const connectionAuthReady = useMemo(() => {
    if (!selectedConnection) {
      return true;
    }

    const authType = selectedConnection.authType || 'none';
    if (authType === 'none') {
      return true;
    }

    return selectedConnection.authConfigured;
  }, [selectedConnection]);

  const canPreviewOrImportSpec = useMemo(() => {
    if (specImportMode === 'url') return specUrl.trim().length > 0;
    if (specImportMode === 'file') return specFile !== null;
    return specContent.trim().length > 0;
  }, [specImportMode, specUrl, specFile, specContent]);

  const scrollToConnectionAuth = useCallback(() => {
    setWorkspaceTab('setup');
    const section = document.getElementById(CONNECTION_AUTH_SECTION_ID);
    section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    if (section) {
      section.classList.add(styles.authSectionHighlight);
      window.setTimeout(() => section.classList.remove(styles.authSectionHighlight), 2000);
    }
  }, []);

  const loadConnectionIntoForm = useCallback((connection: ApiToolConnection | null) => {
    if (!connection) {
      setConnectionForm(emptyConnectionForm);
      setSpecContent('');
      setBaseUrlOverride('');
      setSpecPreviewResult(null);
      setSelectedOperationIds([]);
      setConnectionTestPath('/');
      setConnectionTestMethod('GET');
      setConnectionTestResult('');
      return;
    }

    setConnectionForm({
      connectionId: connection.connectionId,
      displayName: connection.displayName,
      description: connection.description,
      baseUrl: connection.baseUrl,
      authType: connection.authType || 'none',
      authHeaderName: connection.authHeaderName || 'X-Api-Key',
      authBaseUrl: connection.authBaseUrl || '',
      authTokenUrl: connection.authTokenUrl || '',
      authValue: '',
      authBasicUsername: '',
      authBasicPassword: '',
      clearAuthValue: false,
      oauthGrantType: connection.oauthGrantType || 'authorizationCode',
      oauthAuthorizationUrl: connection.oauthAuthorizationUrl || '',
      oauthTokenUrl: connection.oauthTokenUrl || '',
      oauthScopes: connection.oauthScopes || '',
      oauthClientId: connection.oauthClientId || '',
      oauthClientSecret: '',
      clearOAuthClientSecret: false,
      clearOAuthTokens: false,
      defaultHeaders: connection.defaultHeaders.length > 0
        ? connection.defaultHeaders.map((header) => ({ ...header }))
        : [],
      authKeyLocation: connection.authKeyLocation === 'query' ? 'query' : 'header',
    });
    setSpecUrl(connection.specUrl || '');
    setSpecContent('');
    setBaseUrlOverride('');
    setSpecPreviewResult(null);
    setSelectedOperationIds([]);
    setConnectionTestPath('/');
    setConnectionTestMethod('GET');
    setConnectionTestResult('');
  }, []);

  const loadToolIntoForm = useCallback((tool: ApiToolDefinition | null) => {
    if (!tool) {
      setSelectedToolId('');
      setToolForm(emptyToolForm);
      setPreviewArgs({});
      setToolPreviewResult('');
      return;
    }

    setShowManualToolForm(false);
    setSelectedToolId(tool.toolId);
    setToolForm({
      toolId: tool.toolId,
      connectionId: tool.connectionId,
      operationId: tool.operationId,
      kernelFunctionName: tool.kernelFunctionName,
      displayName: tool.displayName,
      description: tool.description,
      httpMethod: tool.httpMethod,
      pathTemplate: tool.pathTemplate,
      isPublished: tool.isPublished,
      functionId: tool.functionId,
      parameterBindings: tool.parameterBindings,
      responseMode: tool.responseMode || 'full',
      responseJsonPath: tool.responseJsonPath || '',
      assignedUseCaseIds: [],
      requestContentType: tool.requestContentType || 'application/json',
    });
    setPreviewArgs(
      Object.fromEntries(
        tool.parameterBindings.map((binding) => [binding.name, binding.example || binding.defaultValue || '']),
      ),
    );
    setToolPreviewResult('');
  }, []);

  useEffect(() => {
    if (!selectedToolId || !toolForm.functionId) {
      return undefined;
    }

    let cancelled = false;
    void getToolUseCaseAssignments(toolForm.functionId)
      .then((assignedUseCaseIds) => {
        if (!cancelled) {
          setToolForm((current) => (
            current.toolId === selectedToolId
              ? { ...current, assignedUseCaseIds }
              : current
          ));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setToolForm((current) => (
            current.toolId === selectedToolId
              ? { ...current, assignedUseCaseIds: [] }
              : current
          ));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [selectedToolId, toolForm.functionId]);

  const loadData = useCallback(async (preserveSelection = true) => {
    setLoading(true);
    try {
      const [nextConnections, nextTools, nextToolPlugins] = await Promise.all([
        listApiToolConnections(),
        listApiTools(),
        listAiToolPlugins().catch(() => [] as AiToolPluginDefinition[]),
      ]);
      setConnections(nextConnections);
      setTools(nextTools);
      setToolPlugins(nextToolPlugins);

      setSelectedConnectionId((current) => {
        if (preserveSelection && current && nextConnections.some((item) => item.connectionId === current)) {
          return current;
        }
        return nextConnections[0]?.connectionId ?? null;
      });
    } catch {
      showToast(t('failedToLoad'), 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, t]);

  useEffect(() => {
    void loadData(false);
  }, [loadData]);

  const loadedConnectionIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (loadedConnectionIdRef.current === selectedConnectionId) {
      return;
    }

    const connection = connections.find((item) => item.connectionId === selectedConnectionId) ?? null;
    if (selectedConnectionId && !connection) {
      return;
    }

    loadedConnectionIdRef.current = selectedConnectionId;
    loadConnectionIntoForm(connection);
    loadToolIntoForm(null);
    setWorkspaceTab('setup');
    setShowManualToolForm(false);
    setSelectedOperationIds([]);
    setCreateOperationsAsPublished(false);
    setSelectedConnectionToolIds([]);
  }, [connections, selectedConnectionId, loadConnectionIntoForm, loadToolIntoForm]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get('apiToolOAuth');
    if (!status) return;

    const connectionId = params.get('connectionId');
    if (connectionId) {
      setSelectedConnectionId(connectionId);
      setWorkspaceTab('setup');
    }

    const messageKey = status === 'success'
      ? 'oauthConnected'
      : status === 'no_refresh_token'
        ? 'oauthNoRefreshToken'
        : 'oauthFailed';
    showToast(t(messageKey), status === 'success' ? 'success' : 'error');

    params.delete('apiToolOAuth');
    params.delete('connectionId');
    const nextQuery = params.toString();
    const nextUrl = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}`;
    window.history.replaceState({}, '', nextUrl);
  }, [showToast, t]);

  const resetToCreate = () => {
    setSelectedConnectionId(null);
    loadConnectionIntoForm(null);
    loadToolIntoForm(null);
  };

  const handleSaveConnection = async (): Promise<boolean> => {
    if (!canManage) return false;

    const credentialConfigured = Boolean(
      selectedConnection
      && selectedConnection.authConfigured
      && (selectedConnection.authType || 'none') === connectionForm.authType,
    );
    const authIssue = validateConnectionAuth(connectionForm, {
      credentialConfigured,
      oauthSecretConfigured: Boolean(
        connectionForm.authType === 'oauth' && selectedConnection?.oauthClientSecretConfigured,
      ),
    });
    if (authIssue) {
      showToast(t(authIssue), 'error');
      return false;
    }

    const keepExisting = Boolean(credentialConfigured && !connectionForm.clearAuthValue);
    setSaving(true);
    try {
      if (selectedConnectionId) {
        await updateApiToolConnection(
          selectedConnectionId,
          buildUpdateConnectionRequest(connectionForm, { keepExisting }),
        );
        showToast(t('updated'), 'success');
        setConnectionForm((current) => ({
          ...current,
          authValue: '',
          authBasicUsername: '',
          authBasicPassword: '',
          oauthClientSecret: '',
          clearAuthValue: false,
          clearOAuthClientSecret: false,
          clearOAuthTokens: false,
        }));
      } else {
        const created = await createApiToolConnection(buildCreateConnectionRequest(connectionForm));
        setSelectedConnectionId(created.connectionId);
        showToast(t('created'), 'success');
      }
      await loadData();
      return true;
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToSave'), 'error');
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteConnection = async () => {
    if (!canManage || !selectedConnectionId) return;
    if (!window.confirm(t('confirmDeleteConnection'))) return;
    setSaving(true);
    try {
      await deleteApiToolConnection(selectedConnectionId);
      showToast(t('deleted'), 'success');
      resetToCreate();
      await loadData(false);
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToDelete'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewSpec = async () => {
    if (!canManage) return;
    setPreviewing(true);
    try {
      let result: ImportOpenApiSpecResult;
      if (specImportMode === 'url') {
        if (!specUrl.trim()) return;
        result = await previewImportOpenApiSpecFromUrl(
          specUrl.trim(),
          baseUrlOverride.trim() || undefined,
        );
      } else if (specImportMode === 'file') {
        if (!specFile) return;
        result = await previewImportOpenApiSpecUpload(
          specFile,
          baseUrlOverride.trim() || undefined,
        );
      } else {
        if (!specContent.trim()) return;
        result = await previewImportOpenApiSpec(
          specContent.trim(),
          baseUrlOverride.trim() || undefined,
        );
      }
      setSpecPreviewResult(result);
      if (result.suggestedAuth) {
        setConnectionForm((current) => applySuggestedAuthToForm(current, result.suggestedAuth));
      }
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToPreviewSpec'), 'error');
    } finally {
      setPreviewing(false);
    }
  };

  const handleTestConnection = async () => {
    if (!canManage || !selectedConnectionId) return;
    setTestingConnection(true);
    try {
      const path = connectionTestPath.trim() || '/';
      const result = await testApiToolConnection(selectedConnectionId, {
        path,
        httpMethod: connectionTestMethod || 'GET',
      });
      setConnectionTestResult(result);
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToTestConnection'), 'error');
    } finally {
      setTestingConnection(false);
    }
  };

  const handleImportSpec = async () => {
    if (!canManage || !selectedConnectionId) return;
    setImporting(true);
    try {
      let result: { connection: ApiToolConnection; import: ImportOpenApiSpecResult };
      if (specImportMode === 'url') {
        if (!specUrl.trim()) return;
        result = await importSpecFromUrlToConnection(
          selectedConnectionId,
          specUrl.trim(),
          baseUrlOverride.trim() || undefined,
        );
      } else if (specImportMode === 'file') {
        if (!specFile) return;
        result = await importSpecUploadToConnection(
          selectedConnectionId,
          specFile,
          baseUrlOverride.trim() || undefined,
        );
      } else {
        if (!specContent.trim()) return;
        result = await importSpecToConnection(
          selectedConnectionId,
          specContent.trim(),
          baseUrlOverride.trim() || undefined,
        );
      }
      setSpecPreviewResult(result.import);
      if (result.connection.baseUrl) {
        setConnectionForm((current) => ({
          ...current,
          baseUrl: result.connection.baseUrl,
        }));
      }
      const missingNames = result.import.missingTools
        .map((tool) => tool.displayName || tool.operationId)
        .filter(Boolean);
      showToast(
        result.import.updatedToolCount > 0
          ? t('toolsUpdatedFromSpec', { count: result.import.updatedToolCount })
          : t('imported'),
        'success',
      );
      if (missingNames.length > 0) {
        showToast(t('toolsMissingFromSpec', { names: missingNames.join(', ') }), 'warning');
      }
      await loadData();
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToImport'), 'error');
    } finally {
      setImporting(false);
    }
  };

  const handleConnectOAuth = async () => {
    if (!canManage || !selectedConnectionId) return;
    const saved = await handleSaveConnection();
    if (!saved) return;
    setConnectingOAuth(true);
    try {
      const authorizeUrl = await startApiToolOAuth(selectedConnectionId);
      if (!authorizeUrl) {
        showToast(t('oauthFailed'), 'error');
        return;
      }
      window.location.assign(authorizeUrl);
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('oauthFailed'), 'error');
    } finally {
      setConnectingOAuth(false);
    }
  };

  const handleDisconnectOAuth = async () => {
    if (!canManage || !selectedConnectionId) return;
    setConnectingOAuth(true);
    try {
      await disconnectApiToolOAuth(selectedConnectionId);
      showToast(t('oauthDisconnected'), 'success');
      await loadData();
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('oauthFailed'), 'error');
    } finally {
      setConnectingOAuth(false);
    }
  };

  const openManualToolForm = useCallback(() => {
    loadToolIntoForm(null);
    setManualToolForm(emptyManualToolForm);
    setShowManualToolForm(true);
  }, [loadToolIntoForm]);

  const closeManualToolForm = useCallback(() => {
    setShowManualToolForm(false);
    setManualToolForm(emptyManualToolForm);
  }, []);

  const handleCreateManualTool = async () => {
    if (!canManage || !selectedConnectionId) return;
    setSaving(true);
    try {
      const created = await createApiTool({
        toolId: manualToolForm.toolId.trim() || undefined,
        connectionId: selectedConnectionId,
        kernelFunctionName: manualToolForm.kernelFunctionName.trim(),
        displayName: manualToolForm.displayName.trim(),
        description: manualToolForm.description.trim(),
        httpMethod: manualToolForm.httpMethod,
        pathTemplate: manualToolForm.pathTemplate.trim(),
        parameterBindings: normalizeParameterBindingsForSave(manualToolForm.parameterBindings),
        responseMode: manualToolForm.responseMode,
        responseJsonPath: manualToolForm.responseJsonPath.trim(),
        requestContentType: manualToolForm.requestContentType,
        isPublished: manualToolForm.isPublished,
        assignedUseCaseIds: manualToolForm.assignedUseCaseIds,
      });
      closeManualToolForm();
      showToast(t('manualToolCreated'), 'success');
      await loadData();
      loadToolIntoForm(created);
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToCreateTools'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleOperation = (operationId: string) => {
    setSelectedOperationIds((current) => (
      current.includes(operationId)
        ? current.filter((id) => id !== operationId)
        : [...current, operationId]
    ));
  };

  const availableOperationIds = useMemo(() => {
    if (!selectedConnection) return [];
    return selectedConnection.importedOperations
      .filter((operation) => !existingOperationIds.has(operation.operationId))
      .map((operation) => operation.operationId);
  }, [selectedConnection, existingOperationIds]);

  const allAvailableOperationsSelected = availableOperationIds.length > 0
    && availableOperationIds.every((operationId) => selectedOperationIds.includes(operationId));

  const toggleAllAvailableOperations = () => {
    setSelectedOperationIds(allAvailableOperationsSelected ? [] : availableOperationIds);
  };

  const allConnectionToolsSelected = connectionTools.length > 0
    && connectionTools.every((tool) => selectedConnectionToolIds.includes(tool.toolId));

  const toggleConnectionToolSelection = (toolId: string) => {
    setSelectedConnectionToolIds((current) => (
      current.includes(toolId)
        ? current.filter((id) => id !== toolId)
        : [...current, toolId]
    ));
  };

  const toggleAllConnectionTools = () => {
    setSelectedConnectionToolIds(
      allConnectionToolsSelected
        ? []
        : connectionTools.map((tool) => tool.toolId),
    );
  };

  const handleBulkPublishTools = async (isPublished: boolean) => {
    if (!canManage || selectedConnectionToolIds.length === 0) {
      return;
    }

    setBulkPublishing(true);
    try {
      const selectedTools = connectionTools.filter((tool) => selectedConnectionToolIds.includes(tool.toolId));
      await Promise.all(
        selectedTools.map((tool) => updateApiTool(tool.toolId, {
          connectionId: tool.connectionId,
          operationId: tool.operationId,
          kernelFunctionName: tool.kernelFunctionName,
          displayName: tool.displayName,
          description: tool.description,
          httpMethod: tool.httpMethod,
          pathTemplate: tool.pathTemplate,
          parameterBindings: tool.parameterBindings,
          responseMode: tool.responseMode || 'full',
          responseJsonPath: tool.responseJsonPath || '',
          requestContentType: tool.requestContentType || 'application/json',
          isPublished,
          assignedUseCaseIds: bulkAssignedUseCaseIds,
        })),
      );

      showToast(
        isPublished
          ? t('toolsBulkPublished', { count: selectedTools.length })
          : t('toolsBulkUnpublished', { count: selectedTools.length }),
        'success',
      );
      await loadData();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToSave');
      showToast(message, 'error');
      if (isPublished && /authentication|auth/i.test(message)) {
        scrollToConnectionAuth();
      }
    } finally {
      setBulkPublishing(false);
    }
  };

  const handleCreateSelectedTools = async () => {
    if (!canManage || !selectedConnection || selectedOperationIds.length === 0) return;
    setSaving(true);
    try {
      const operationsById = new Map(
        selectedConnection.importedOperations.map((operation) => [operation.operationId, operation]),
      );
      let createdCount = 0;

      for (const operationId of selectedOperationIds) {
        if (existingOperationIds.has(operationId)) continue;
        const operation = operationsById.get(operationId);
        if (!operation) continue;

        await createApiTool({
          connectionId: selectedConnection.connectionId,
          operationId: operation.operationId,
          kernelFunctionName: operation.suggestedKernelFunctionName,
          displayName: operation.suggestedDisplayName || operation.summary,
          description: operation.description || operation.summary,
          httpMethod: operation.httpMethod,
          pathTemplate: operation.pathTemplate,
          parameterBindings: operation.parameterBindings,
          responseMode: 'full',
          requestContentType: operation.requestContentType || 'application/json',
          isPublished: createOperationsAsPublished,
          assignedUseCaseIds: createOperationAssignedUseCaseIds,
        });
        createdCount += 1;
      }

      showToast(t('toolsCreated', { count: createdCount }), 'success');
      setSelectedOperationIds([]);
      setWorkspaceTab('tools');
      await loadData();
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToCreateTools'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTool = async () => {
    if (!canManage || !selectedToolId) return;
    setSaving(true);
    try {
      await updateApiTool(selectedToolId, {
        connectionId: toolForm.connectionId,
        operationId: toolForm.operationId,
        kernelFunctionName: toolForm.kernelFunctionName.trim(),
        displayName: toolForm.displayName.trim(),
        description: toolForm.description.trim(),
        httpMethod: toolForm.httpMethod,
        pathTemplate: toolForm.pathTemplate.trim(),
        parameterBindings: isManualTool
          ? normalizeParameterBindingsForSave(toolForm.parameterBindings)
          : toolForm.parameterBindings,
        responseMode: toolForm.responseMode,
        responseJsonPath: toolForm.responseJsonPath.trim(),
        requestContentType: toolForm.requestContentType,
        isPublished: toolForm.isPublished,
        assignedUseCaseIds: toolForm.assignedUseCaseIds,
      });
      showToast(t('updated'), 'success');
      await loadData();
    } catch (error) {
      const message = error instanceof Error ? error.message : t('failedToSave');
      showToast(message, 'error');
      if (toolForm.isPublished && /authentication|auth/i.test(message)) {
        scrollToConnectionAuth();
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePreviewTool = async () => {
    if (!canManage || !selectedToolId) return;
    setPreviewingTool(true);
    try {
      const result = await previewApiTool(selectedToolId, previewArgs);
      setToolPreviewResult(result);
    } catch (error) {
      showToast(error instanceof Error ? error.message : t('failedToPreviewTool'), 'error');
    } finally {
      setPreviewingTool(false);
    }
  };

  const handleDeleteTool = async () => {
    if (!canManage || !selectedToolId) return;
    if (!window.confirm(t('confirmDeleteTool'))) return;
    setSaving(true);
    try {
      await deleteApiTool(selectedToolId);
      showToast(t('deleted'), 'success');
      loadToolIntoForm(null);
      await loadData();
    } catch {
      showToast(t('failedToDelete'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const renderOperation = (operation: ApiToolImportedOperation) => {
    const hasTool = existingOperationIds.has(operation.operationId);
    const checked = selectedOperationIds.includes(operation.operationId);

    return (
      <label
        key={operation.operationId}
        className={`${styles.operationItem} ${hasTool ? styles.operationItemExisting : ''}`}
      >
        <input
          type="checkbox"
          checked={checked}
          disabled={!canManage || hasTool}
          onChange={() => toggleOperation(operation.operationId)}
        />
        <div className={styles.operationMain}>
          <div className={styles.operationTitle}>
            <span className={styles.methodBadge}>{operation.httpMethod}</span>
            <span style={{ marginLeft: 8 }}>{operation.pathTemplate}</span>
            {hasTool ? <span className={styles.existingBadge}>{t('alreadyHasTool')}</span> : null}
          </div>
          <div className={styles.operationMeta}>
            {operation.summary || operation.operationId}
            {operation.parameterBindings.length > 0
              ? ` · ${operation.parameterBindings.length} ${t('parameterBindings').toLowerCase()}`
              : null}
          </div>
        </div>
      </label>
    );
  };

  const showConnectionEditor = canManage || selectedConnectionId !== null;

  const pageContent = (
    <>
      {!embedded ? (
        <>
          <Breadcrumb
            items={[
              { label: t('ai', { ns: 'nav' }), path: '/ai/knowledge' },
              { label: t('tools', { ns: 'nav' }), path: '/ai/tools' },
              { label: t('tabApi', { ns: 'tools' }), path: '/ai/tools/api' },
            ]}
          />
          <div className="company-page-head company-page-head--row">
            <div>
              <h2>{t('title')}</h2>
              <p className="company-page-head-description">{t('subtitle')}</p>
            </div>
            {canManage ? (
              <button type="button" className="btn btn-secondary" onClick={resetToCreate}>
                {t('createConnection')}
              </button>
            ) : null}
          </div>
        </>
      ) : canManage ? (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
          <button type="button" className="btn btn-secondary" onClick={resetToCreate}>
            {t('createConnection')}
          </button>
        </div>
      ) : null}

      {!canManage ? <p style={{ color: '#64748b' }}>{t('readOnlyHint')}</p> : null}

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', marginTop: 24 }}>
          <Spinner text={t('loading', { ns: 'common' })} />
        </div>
      ) : (
        <div className={styles.pageLayout}>
          <section className="company-section">
            <h3>{t('connectionList')}</h3>
            <div style={{ display: 'grid', gap: 8, marginTop: 12 }}>
              {connections.map((connection) => (
                <button
                  key={connection.connectionId}
                  type="button"
                  className={`btn btn-secondary ${styles.connectionButton} ${selectedConnectionId === connection.connectionId ? styles.connectionButtonActive : ''}`}
                  onClick={() => setSelectedConnectionId(connection.connectionId)}
                >
                  <strong>{connection.displayName}</strong>
                  <div className={styles.connectionMeta}>
                    {connection.baseUrl || t('noBaseUrl')}
                    {connection.authConfigured ? ` · ${t('authConfiguredShort')}` : null}
                    {connection.importedOperations.length > 0
                      ? ` · ${t('operationCount', { count: connection.importedOperations.length })}`
                      : null}
                  </div>
                </button>
              ))}
              {connections.length === 0 ? <div>{t('noConnections')}</div> : null}
            </div>
          </section>

          <section className="company-section">
            {showConnectionEditor ? (
              <>
                <div className={styles.connectionSummary}>
                  <div>
                    <h3 style={{ margin: 0 }}>
                      {selectedConnectionId ? t('editConnection') : t('createConnection')}
                    </h3>
                    {selectedConnection?.baseUrl ? (
                      <p className={styles.connectionMeta} style={{ margin: '4px 0 0' }}>
                        {selectedConnection.baseUrl}
                      </p>
                    ) : null}
                  </div>
                  {selectedConnection ? (
                    <div className={styles.connectionSummaryMeta}>
                      {selectedConnection.authConfigured || selectedConnection.oauthConnected ? (
                        <span className={`${styles.summaryBadge} ${styles.summaryBadgeSuccess}`}>
                          {t('authConfiguredShort')}
                        </span>
                      ) : null}
                      {selectedConnection.importedOperations.length > 0 ? (
                        <span className={styles.summaryBadge}>
                          {t('operationCount', { count: selectedConnection.importedOperations.length })}
                        </span>
                      ) : null}
                      <span className={styles.summaryBadge}>
                        {t('toolCount', { count: connectionTools.length })}
                      </span>
                    </div>
                  ) : null}
                </div>

                {selectedConnectionId ? (
                  <nav className={styles.tabBar} aria-label={t('workspaceTabsLabel')}>
                    <button
                      type="button"
                      className={`${styles.tab} ${workspaceTab === 'setup' ? styles.tabActive : ''}`}
                      onClick={() => setWorkspaceTab('setup')}
                    >
                      {t('tabSetup')}
                    </button>
                    <button
                      type="button"
                      className={`${styles.tab} ${workspaceTab === 'import' ? styles.tabActive : ''}`}
                      onClick={() => setWorkspaceTab('import')}
                    >
                      {t('tabImport')}
                      {selectedConnection && selectedConnection.importedOperations.length > 0 ? (
                        <span className={styles.tabBadge}>{selectedConnection.importedOperations.length}</span>
                      ) : null}
                    </button>
                    <button
                      type="button"
                      className={`${styles.tab} ${workspaceTab === 'tools' ? styles.tabActive : ''}`}
                      onClick={() => setWorkspaceTab('tools')}
                    >
                      {t('tabTools')}
                      {connectionTools.length > 0 ? (
                        <span className={styles.tabBadge}>{connectionTools.length}</span>
                      ) : null}
                    </button>
                  </nav>
                ) : (
                  <p className={styles.fieldHint} style={{ marginTop: 12 }}>{t('newConnectionHint')}</p>
                )}

                {(!selectedConnectionId || workspaceTab === 'setup') ? (
                <div className={`${styles.tabPanel} company-form-grid`} style={{ borderTop: 'none' }}>
                  {!selectedConnectionId ? (
                    <FormField
                      id="connection-id"
                      label={t('connectionId')}
                      hint={t('connectionIdHint')}
                    >
                      <input
                        id="connection-id"
                        className="text-input"
                        value={connectionForm.connectionId}
                        disabled={!canManage || saving}
                        onChange={(event) => setConnectionForm((current) => ({
                          ...current,
                          connectionId: event.target.value,
                        }))}
                        placeholder={t('connectionIdPlaceholder')}
                        autoComplete="off"
                        spellCheck={false}
                      />
                    </FormField>
                  ) : null}

                  <FormField
                    id="connection-display-name"
                    label={(
                      <>
                        {t('displayName')}
                        <span style={{ color: '#dc2626' }}> *</span>
                      </>
                    )}
                  >
                    <input
                      id="connection-display-name"
                      className="text-input"
                      value={connectionForm.displayName}
                      disabled={!canManage || saving}
                      onChange={(event) => setConnectionForm((current) => ({
                        ...current,
                        displayName: event.target.value,
                      }))}
                      placeholder={t('displayNamePlaceholder')}
                      autoComplete="off"
                    />
                  </FormField>

                  <FormField
                    id="connection-base-url"
                    label={t('baseUrl')}
                    hint={t('baseUrlHint')}
                  >
                    <input
                      id="connection-base-url"
                      className="text-input"
                      type="url"
                      value={connectionForm.baseUrl}
                      disabled={!canManage || saving}
                      onChange={(event) => setConnectionForm((current) => ({
                        ...current,
                        baseUrl: event.target.value,
                      }))}
                      placeholder="https://api.example.com/v1"
                      autoComplete="off"
                      spellCheck={false}
                    />
                  </FormField>

                  <FormField
                    id="connection-description"
                    label={t('description')}
                    fullWidth
                  >
                    <textarea
                      id="connection-description"
                      className="text-input"
                      rows={3}
                      value={connectionForm.description}
                      disabled={!canManage || saving}
                      onChange={(event) => setConnectionForm((current) => ({
                        ...current,
                        description: event.target.value,
                      }))}
                      placeholder={t('descriptionPlaceholder')}
                    />
                  </FormField>

                  <div className={`${styles.formFieldFull} ${styles.sectionBlock}`}>
                    <h4 className={styles.sectionTitle}>{t('defaultHeaders')}</h4>
                    <p className={styles.fieldHint}>{t('defaultHeadersHint')}</p>
                    {connectionForm.defaultHeaders.map((header, index) => (
                      <div key={`default-header-${index}`} className={styles.defaultHeaderRow}>
                        <input
                          className="text-input"
                          value={header.name}
                          disabled={!canManage || saving}
                          onChange={(event) => setConnectionForm((current) => ({
                            ...current,
                            defaultHeaders: current.defaultHeaders.map((item, itemIndex) => (
                              itemIndex === index
                                ? { ...item, name: event.target.value }
                                : item
                            )),
                          }))}
                          placeholder={t('defaultHeaderNamePlaceholder')}
                          autoComplete="off"
                          spellCheck={false}
                        />
                        <input
                          className="text-input"
                          value={header.value}
                          disabled={!canManage || saving}
                          onChange={(event) => setConnectionForm((current) => ({
                            ...current,
                            defaultHeaders: current.defaultHeaders.map((item, itemIndex) => (
                              itemIndex === index
                                ? { ...item, value: event.target.value }
                                : item
                            )),
                          }))}
                          placeholder={t('defaultHeaderValuePlaceholder')}
                          autoComplete="off"
                          spellCheck={false}
                        />
                        <button
                          type="button"
                          className="btn btn-secondary"
                          disabled={!canManage || saving}
                          onClick={() => setConnectionForm((current) => ({
                            ...current,
                            defaultHeaders: current.defaultHeaders.filter((_, itemIndex) => itemIndex !== index),
                          }))}
                        >
                          {t('removeDefaultHeader')}
                        </button>
                      </div>
                    ))}
                    <div className={styles.actionsRow}>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        disabled={!canManage || saving}
                        onClick={() => setConnectionForm((current) => ({
                          ...current,
                          defaultHeaders: [...current.defaultHeaders, createEmptyDefaultHeader()],
                        }))}
                      >
                        {t('addDefaultHeader')}
                      </button>
                    </div>
                  </div>

                  <div id={CONNECTION_AUTH_SECTION_ID} className={`${styles.formFieldFull} company-form-grid`}>
                  {(connectionForm.authType === 'bearer' || connectionForm.authType === 'basic' || connectionForm.authType === 'oauth') ? (
                    <FormField
                      id="connection-auth-base-url"
                      label={t('authBaseUrl')}
                      hint={t('authBaseUrlHint')}
                      fullWidth
                    >
                      <input
                        id="connection-auth-base-url"
                        className="text-input"
                        type="url"
                        value={connectionForm.authBaseUrl}
                        disabled={!canManage || saving}
                        onChange={(event) => setConnectionForm((current) => ({
                          ...current,
                          authBaseUrl: event.target.value,
                        }))}
                        placeholder="https://auth.example.com"
                        autoComplete="off"
                        spellCheck={false}
                      />
                    </FormField>
                  ) : null}

                  <FormField id="connection-auth-type" label={t('authType')}>
                    <select
                      id="connection-auth-type"
                      className="text-input"
                      value={connectionForm.authType}
                      disabled={!canManage || saving}
                      onChange={(event) => {
                        const authType = event.target.value;
                        setConnectionForm((current) => ({
                          ...current,
                          authType,
                          authValue: '',
                          authBasicUsername: '',
                          authBasicPassword: '',
                          clearAuthValue: false,
                          authTokenUrl: authType === 'bearer' || authType === 'basic' ? current.authTokenUrl : '',
                          authBaseUrl: authType === 'bearer' || authType === 'basic' || authType === 'oauth'
                            ? current.authBaseUrl
                            : '',
                        }));
                      }}
                    >
                      <option value="none">{t('authTypeNone')}</option>
                      <option value="apiKey">{t('authTypeApiKey')}</option>
                      <option value="bearer">{t('authTypeBearer')}</option>
                      <option value="basic">{t('authTypeBasic')}</option>
                      <option value="oauth">{t('authTypeOAuth')}</option>
                    </select>
                  </FormField>

                  {connectionForm.authType === 'apiKey' ? (
                    <FormField id="connection-auth-key-location" label={t('authKeyLocation')}>
                      <select
                        id="connection-auth-key-location"
                        className="text-input"
                        value={connectionForm.authKeyLocation}
                        disabled={!canManage || saving}
                        onChange={(event) => setConnectionForm((current) => ({
                          ...current,
                          authKeyLocation: event.target.value,
                        }))}
                      >
                        <option value="header">{t('authKeyLocationHeader')}</option>
                        <option value="query">{t('authKeyLocationQuery')}</option>
                      </select>
                    </FormField>
                  ) : null}

                  {connectionForm.authType === 'apiKey' ? (
                    <FormField
                      id="connection-auth-header"
                      label={connectionForm.authKeyLocation === 'query' ? t('authKeyName') : t('authHeaderName')}
                    >
                      <input
                        id="connection-auth-header"
                        className="text-input"
                        value={connectionForm.authHeaderName}
                        disabled={!canManage || saving}
                        onChange={(event) => setConnectionForm((current) => ({
                          ...current,
                          authHeaderName: event.target.value,
                        }))}
                        placeholder={t('authHeaderNamePlaceholder')}
                        autoComplete="off"
                        spellCheck={false}
                      />
                    </FormField>
                  ) : null}

                  {connectionForm.authType === 'basic' ? (
                    <>
                      <FormField
                        id="connection-auth-basic-username"
                        label={t('authBasicUsername')}
                        hint={connectionForm.authTokenUrl.trim()
                          ? t('authBasicHintToken')
                          : t('authBasicHint')}
                      >
                        <input
                          id="connection-auth-basic-username"
                          className="text-input"
                          type="text"
                          value={connectionForm.authBasicUsername}
                          disabled={!canManage || saving}
                          onChange={(event) => setConnectionForm((current) => ({
                            ...current,
                            authBasicUsername: event.target.value,
                          }))}
                          placeholder={selectedConnection?.authConfigured && selectedConnection.authType === 'basic'
                            ? t('authBasicUsernamePlaceholderConfigured')
                            : t('authBasicUsernamePlaceholder')}
                          autoComplete="username"
                          spellCheck={false}
                        />
                      </FormField>
                      <FormField id="connection-auth-basic-password" label={t('authBasicPassword')}>
                        <input
                          id="connection-auth-basic-password"
                          className="text-input"
                          type="password"
                          value={connectionForm.authBasicPassword}
                          disabled={!canManage || saving}
                          onChange={(event) => setConnectionForm((current) => ({
                            ...current,
                            authBasicPassword: event.target.value,
                          }))}
                          placeholder={selectedConnection?.authConfigured && selectedConnection.authType === 'basic'
                            ? t('authBasicPasswordPlaceholderConfigured')
                            : t('authBasicPasswordPlaceholder')}
                          autoComplete="current-password"
                        />
                      </FormField>
                    </>
                  ) : null}

                  {connectionForm.authType === 'bearer' || connectionForm.authType === 'basic' ? (
                    <FormField
                      id="connection-auth-token-url"
                      label={t('authTokenUrl')}
                      hint={connectionForm.authType === 'basic'
                        ? t('authTokenUrlHintBasic')
                        : t('authTokenUrlHint')}
                      fullWidth
                    >
                      <input
                        id="connection-auth-token-url"
                        className="text-input"
                        value={connectionForm.authTokenUrl}
                        disabled={!canManage || saving}
                        onChange={(event) => setConnectionForm((current) => ({
                          ...current,
                          authTokenUrl: event.target.value,
                        }))}
                        placeholder={t('authTokenUrlPlaceholder')}
                        autoComplete="off"
                        spellCheck={false}
                      />
                    </FormField>
                  ) : null}

                  {connectionForm.authType === 'apiKey' || connectionForm.authType === 'bearer' ? (
                    <FormField
                      id="connection-auth-value"
                      label={connectionForm.authType === 'bearer' && connectionForm.authTokenUrl.trim()
                        ? t('authClientCredentials')
                        : connectionForm.authType === 'bearer'
                          ? t('authBearerToken')
                          : t('authValue')}
                      hint={connectionForm.authType === 'bearer' && connectionForm.authTokenUrl.trim()
                        ? t('authClientCredentialsHint')
                        : authValueHint}
                      fullWidth={connectionForm.authType === 'bearer'}
                    >
                      <input
                        id="connection-auth-value"
                        className="text-input"
                        type="password"
                        value={connectionForm.authValue}
                        disabled={!canManage || saving}
                        onChange={(event) => setConnectionForm((current) => ({
                          ...current,
                          authValue: event.target.value,
                        }))}
                        placeholder={connectionForm.authType === 'bearer' && connectionForm.authTokenUrl.trim()
                          ? t('authClientCredentialsPlaceholder')
                          : selectedConnection?.authConfigured && (selectedConnection.authType || 'none') === connectionForm.authType
                            ? t('authValuePlaceholderConfigured')
                            : t('authValuePlaceholder')}
                        autoComplete="new-password"
                      />
                    </FormField>
                  ) : null}

                  {connectionForm.authType === 'oauth' ? (
                    <>
                      <FormField id="oauth-grant-type" label={t('oauthGrantType')}>
                        <select
                          id="oauth-grant-type"
                          className="text-input"
                          value={connectionForm.oauthGrantType}
                          disabled={!canManage || saving}
                          onChange={(event) => setConnectionForm((current) => ({
                            ...current,
                            oauthGrantType: event.target.value,
                          }))}
                        >
                          <option value="authorizationCode">{t('oauthGrantAuthorizationCode')}</option>
                          <option value="clientCredentials">{t('oauthGrantClientCredentials')}</option>
                        </select>
                      </FormField>
                      {connectionForm.oauthGrantType === 'authorizationCode' ? (
                        <FormField id="oauth-authorization-url" label={t('oauthAuthorizationUrl')} fullWidth>
                          <input
                            id="oauth-authorization-url"
                            className="text-input"
                            value={connectionForm.oauthAuthorizationUrl}
                            disabled={!canManage || saving}
                            onChange={(event) => setConnectionForm((current) => ({
                              ...current,
                              oauthAuthorizationUrl: event.target.value,
                            }))}
                            placeholder="https://provider.example.com/oauth/authorize"
                          />
                        </FormField>
                      ) : null}
                      <FormField id="oauth-token-url" label={t('oauthTokenUrl')} hint={t('oauthTokenUrlHint')} fullWidth>
                        <input
                          id="oauth-token-url"
                          className="text-input"
                          value={connectionForm.oauthTokenUrl}
                          disabled={!canManage || saving}
                          onChange={(event) => setConnectionForm((current) => ({
                            ...current,
                            oauthTokenUrl: event.target.value,
                          }))}
                          placeholder="https://provider.example.com/oauth/token"
                        />
                      </FormField>
                      <FormField id="oauth-scopes" label={t('oauthScopes')} fullWidth>
                        <input
                          id="oauth-scopes"
                          className="text-input"
                          value={connectionForm.oauthScopes}
                          disabled={!canManage || saving}
                          onChange={(event) => setConnectionForm((current) => ({
                            ...current,
                            oauthScopes: event.target.value,
                          }))}
                          placeholder="read write"
                        />
                      </FormField>
                      <FormField id="oauth-client-id" label={t('oauthClientId')}>
                        <input
                          id="oauth-client-id"
                          className="text-input"
                          value={connectionForm.oauthClientId}
                          disabled={!canManage || saving}
                          onChange={(event) => setConnectionForm((current) => ({
                            ...current,
                            oauthClientId: event.target.value,
                          }))}
                        />
                      </FormField>
                      <FormField id="oauth-client-secret" label={t('oauthClientSecret')}>
                        <input
                          id="oauth-client-secret"
                          className="text-input"
                          type="password"
                          value={connectionForm.oauthClientSecret}
                          disabled={!canManage || saving}
                          onChange={(event) => setConnectionForm((current) => ({
                            ...current,
                            oauthClientSecret: event.target.value,
                          }))}
                          placeholder={selectedConnection?.oauthClientSecretConfigured
                            ? t('oauthClientSecretPlaceholderConfigured')
                            : t('oauthClientSecretPlaceholder')}
                        />
                      </FormField>
                      {selectedConnection?.oauthClientSecretConfigured ? (
                        <label className={styles.authOptionRow}>
                          <input
                            type="checkbox"
                            checked={connectionForm.clearOAuthClientSecret}
                            disabled={!canManage || saving}
                            onChange={(event) => setConnectionForm((current) => ({
                              ...current,
                              clearOAuthClientSecret: event.target.checked,
                              oauthClientSecret: event.target.checked ? '' : current.oauthClientSecret,
                            }))}
                          />
                          {t('clearOAuthClientSecret')}
                        </label>
                      ) : null}
                      {selectedConnectionId && connectionForm.oauthGrantType === 'authorizationCode' ? (
                        <div className={`${styles.actionsRow} ${styles.formFieldFull}`}>
                          <span className={styles.fieldHint}>
                            {selectedConnection?.oauthConnected ? t('oauthConnected') : t('oauthNotConnected')}
                          </span>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            disabled={connectingOAuth || saving}
                            onClick={() => void handleConnectOAuth()}
                          >
                            {connectingOAuth ? t('loading', { ns: 'common' }) : t('oauthConnect')}
                          </button>
                          {selectedConnection?.oauthConnected ? (
                            <button
                              type="button"
                              className="btn btn-secondary"
                              disabled={connectingOAuth || saving}
                              onClick={() => void handleDisconnectOAuth()}
                            >
                              {t('oauthDisconnect')}
                            </button>
                          ) : null}
                        </div>
                      ) : null}
                    </>
                  ) : null}

                  {connectionForm.authType !== 'none' && connectionForm.authType !== 'oauth' ? (
                    selectedConnection?.authConfigured ? (
                      <div className={`${styles.authOptions} ${styles.formFieldFull}`}>
                        <span className={styles.fieldHint}>{t('authConfigured')}</span>
                        <label className={styles.authOptionRow}>
                          <input
                            type="checkbox"
                            checked={connectionForm.clearAuthValue}
                            disabled={!canManage || saving}
                            onChange={(event) => setConnectionForm((current) => ({
                              ...current,
                              clearAuthValue: event.target.checked,
                              authValue: event.target.checked ? '' : current.authValue,
                              authBasicUsername: event.target.checked ? '' : current.authBasicUsername,
                              authBasicPassword: event.target.checked ? '' : current.authBasicPassword,
                            }))}
                          />
                          {t('clearAuthValue')}
                        </label>
                      </div>
                    ) : null
                  ) : null}
                  </div>

                  {canManage && selectedConnectionId ? (
                    <div className="company-form-grid">
                      <FormField id="connection-test-method" label={t('connectionTestMethod')}>
                        <select
                          id="connection-test-method"
                          className="text-input"
                          value={connectionTestMethod}
                          disabled={testingConnection}
                          onChange={(event) => setConnectionTestMethod(event.target.value)}
                        >
                          <option value="GET">GET</option>
                          <option value="HEAD">HEAD</option>
                          <option value="OPTIONS">OPTIONS</option>
                        </select>
                      </FormField>
                      <FormField id="connection-test-path" label={t('connectionTestPath')} hint={t('connectionTestPathHint')}>
                        <input
                          id="connection-test-path"
                          className="text-input"
                          value={connectionTestPath}
                          disabled={testingConnection}
                          onChange={(event) => setConnectionTestPath(event.target.value)}
                          placeholder="/"
                          spellCheck={false}
                        />
                      </FormField>
                    </div>
                  ) : null}

                  {canManage ? (
                    <div className={styles.actionsRow}>
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={saving || !connectionForm.displayName.trim()}
                        onClick={() => void handleSaveConnection()}
                      >
                        {t('saveConnection')}
                      </button>
                      {selectedConnectionId ? (
                        <>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            disabled={testingConnection || !connectionForm.baseUrl.trim()}
                            onClick={() => void handleTestConnection()}
                          >
                            {testingConnection ? t('loading', { ns: 'common' }) : t('testConnection')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            disabled={saving}
                            onClick={() => void handleDeleteConnection()}
                          >
                            {t('deleteConnection')}
                          </button>
                        </>
                      ) : null}
                    </div>
                  ) : null}

                  {connectionTestResult ? (
                    <div className={`${styles.previewPanel} ${styles.formFieldFull}`}>
                      <h4 className={styles.sectionTitle}>{t('connectionTestResult')}</h4>
                      <pre className={styles.previewOutput}>{connectionTestResult}</pre>
                    </div>
                  ) : null}
                </div>
                ) : null}

                {selectedConnectionId && workspaceTab === 'import' ? (
                  <div className={styles.tabPanel}>
                    <div>
                      <h4 className={styles.sectionTitle}>{t('importOpenApi')}</h4>
                      <p style={{ color: '#64748b', fontSize: 13 }}>{t('importOpenApiHelp')}</p>
                      {selectedConnection?.specContentStored ? (
                        <p style={{ color: '#64748b', fontSize: 13 }}>{t('specStored')}</p>
                      ) : null}

                      <div className={styles.actionsRow} style={{ marginTop: 12 }}>
                        {(['paste', 'url', 'file'] as SpecImportMode[]).map((mode) => (
                          <button
                            key={mode}
                            type="button"
                            className={`btn btn-secondary ${specImportMode === mode ? styles.connectionButtonActive : ''}`}
                            disabled={!canManage}
                            onClick={() => setSpecImportMode(mode)}
                          >
                            {t(`specImportMode_${mode}`)}
                          </button>
                        ))}
                      </div>

                      <div className="company-form-grid" style={{ marginTop: 12 }}>
                        {specImportMode === 'paste' ? (
                          <FormField id="spec-content" label={t('specContent')} fullWidth>
                            <textarea
                              id="spec-content"
                              className={`text-input ${styles.specTextarea}`}
                              value={specContent}
                              disabled={!canManage || importing || previewing}
                              onChange={(event) => setSpecContent(event.target.value)}
                              placeholder={t('specContentPlaceholder')}
                              spellCheck={false}
                            />
                          </FormField>
                        ) : null}
                        {specImportMode === 'url' ? (
                          <FormField id="spec-url" label={t('specUrl')} fullWidth>
                            <input
                              id="spec-url"
                              className="text-input"
                              type="url"
                              value={specUrl}
                              disabled={!canManage || importing || previewing}
                              onChange={(event) => setSpecUrl(event.target.value)}
                              placeholder={t('specUrlPlaceholder')}
                            />
                          </FormField>
                        ) : null}
                        {specImportMode === 'file' ? (
                          <FormField id="spec-file" label={t('specFile')} fullWidth>
                            <input
                              id="spec-file"
                              className="text-input"
                              type="file"
                              accept=".json,.yaml,.yml,application/json,text/yaml"
                              disabled={!canManage || importing || previewing}
                              onChange={(event) => setSpecFile(event.target.files?.[0] ?? null)}
                            />
                          </FormField>
                        ) : null}
                        <FormField
                          id="base-url-override"
                          label={t('baseUrlOverride')}
                          hint={t('baseUrlOverrideHint')}
                          fullWidth
                        >
                          <input
                            id="base-url-override"
                            className="text-input"
                            type="url"
                            value={baseUrlOverride}
                            disabled={!canManage || importing || previewing}
                            onChange={(event) => setBaseUrlOverride(event.target.value)}
                            placeholder={t('baseUrlOverridePlaceholder')}
                            autoComplete="off"
                            spellCheck={false}
                          />
                        </FormField>
                      </div>

                      {canManage ? (
                        <div className={styles.actionsRow}>
                          <button
                            type="button"
                            className="btn btn-secondary"
                            disabled={previewing || !canPreviewOrImportSpec}
                            onClick={() => void handlePreviewSpec()}
                          >
                            {previewing ? t('loading', { ns: 'common' }) : t('previewSpec')}
                          </button>
                          <button
                            type="button"
                            className="btn btn-primary"
                            disabled={importing || !canPreviewOrImportSpec}
                            onClick={() => void handleImportSpec()}
                          >
                            {importing ? t('loading', { ns: 'common' }) : t('importSpec')}
                          </button>
                        </div>
                      ) : null}

                      <div className={styles.previewPanel}>
                        {specPreviewResult ? (
                          <>
                            <div>
                              {t('specPreviewResult', {
                                count: specPreviewResult.operationCount,
                                format: specPreviewResult.specFormat,
                                baseUrl: specPreviewResult.baseUrl || t('noBaseUrl'),
                              })}
                            </div>
                            {specPreviewResult.suggestedAuth ? (
                              <div className={styles.fieldHint}>
                                {t('suggestedAuthDetected', {
                                  authType: specPreviewResult.suggestedAuth.authType,
                                  schemeName: specPreviewResult.suggestedAuth.schemeName,
                                })}
                              </div>
                            ) : null}
                            {specPreviewResult.warnings.length > 0 ? (
                              <ul className={styles.warningList}>
                                {specPreviewResult.warnings.map((warning) => (
                                  <li key={warning}>{warning}</li>
                                ))}
                              </ul>
                            ) : null}
                            {specPreviewResult.updatedToolCount > 0 ? (
                              <div className={styles.fieldHint}>
                                {t('toolsUpdatedFromSpec', { count: specPreviewResult.updatedToolCount })}
                              </div>
                            ) : null}
                            {specPreviewResult.missingTools.length > 0 ? (
                              <div className={styles.fieldHint}>
                                {t('toolsMissingFromSpec', {
                                  names: specPreviewResult.missingTools
                                    .map((tool) => tool.displayName || tool.operationId)
                                    .join(', '),
                                })}
                              </div>
                            ) : null}
                          </>
                        ) : (
                          <div>{t('noPreviewYet')}</div>
                        )}
                      </div>
                    </div>

                    {selectedConnection && selectedConnection.importedOperations.length > 0 ? (
                      <div className={styles.sectionBlock}>
                        <h4 className={styles.sectionTitle}>{t('operationsFromSpec')}</h4>
                        <p style={{ color: '#64748b', fontSize: 13 }}>{t('operationsHelp')}</p>
                        {canManage ? (
                          <>
                            <label className={styles.createPublishRow}>
                              <input
                                type="checkbox"
                                checked={createOperationsAsPublished}
                                disabled={saving}
                                onChange={(event) => setCreateOperationsAsPublished(event.target.checked)}
                              />
                              <span>
                                <strong>{t('createAsPublished')}</strong>
                                <span className={styles.createPublishHelp}>{t('createAsPublishedHelp')}</span>
                              </span>
                            </label>
                            {createOperationsAsPublished && !connectionAuthReady ? (
                              <div className={styles.authPreviewAlert} role="alert">
                                <strong>{t('publishAuthRequiredTitle')}</strong>
                                <p>{t('publishAuthRequiredBody')}</p>
                                <button
                                  type="button"
                                  className="btn btn-secondary"
                                  onClick={scrollToConnectionAuth}
                                >
                                  {t('authPreviewUpdateCredentials')}
                                </button>
                              </div>
                            ) : null}
                            <ToolUseCaseAssignment
                              selectedUseCaseIds={createOperationAssignedUseCaseIds}
                              disabled={saving}
                              isPublished={createOperationsAsPublished}
                              onChange={setCreateOperationAssignedUseCaseIds}
                            />
                            <div className={styles.actionsRow}>
                              <button
                                type="button"
                                className="btn btn-secondary"
                                disabled={saving || availableOperationIds.length === 0}
                                onClick={toggleAllAvailableOperations}
                              >
                                {allAvailableOperationsSelected
                                  ? t('deselectAllAvailable')
                                  : t('selectAllAvailable')}
                              </button>
                              <button
                                type="button"
                                className="btn btn-primary"
                                disabled={saving || selectedOperationIds.length === 0}
                                onClick={() => void handleCreateSelectedTools()}
                              >
                                {t('createSelectedTools')}
                              </button>
                            </div>
                          </>
                        ) : null}
                        <div className={styles.operationList}>
                          {selectedConnection.importedOperations.map(renderOperation)}
                        </div>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {selectedConnectionId && workspaceTab === 'tools' ? (
                  <div className={styles.tabPanel}>
                    <div className={styles.toolsWorkspace}>
                      <aside className={styles.toolsSidebar}>
                      <div className={styles.toolsSidebarHeader}>
                        <div>
                          <h4 className={styles.sectionTitle} style={{ margin: 0 }}>{t('toolsListTitle')}</h4>
                          {connectionTools.length > 0 ? (
                            <p className={styles.toolListSummary}>
                              {t('toolListSummary', toolStatusCounts)}
                            </p>
                          ) : null}
                        </div>
                        <div className={styles.toolsSidebarActions}>
                          {canManage && connectionTools.length > 0 ? (
                            <button
                              type="button"
                              className="btn btn-secondary"
                              disabled={bulkPublishing || saving}
                              onClick={toggleAllConnectionTools}
                            >
                              {allConnectionToolsSelected
                                ? t('deselectAllAvailable')
                                : t('selectAllAvailable')}
                            </button>
                          ) : null}
                          {canManage ? (
                            <button
                              type="button"
                              className={[
                                'btn',
                                'btn-secondary',
                                showManualToolForm ? styles.sidebarActionActive : '',
                              ].filter(Boolean).join(' ')}
                              onClick={() => (
                                showManualToolForm ? closeManualToolForm() : openManualToolForm()
                              )}
                            >
                              {showManualToolForm ? t('cancelManualTool') : t('createManualToolShort')}
                            </button>
                          ) : null}
                        </div>
                      </div>

                      {canManage && selectedConnectionToolIds.length > 0 ? (
                        <div className={styles.bulkPublishBlock}>
                          <ToolUseCaseAssignment
                            selectedUseCaseIds={bulkAssignedUseCaseIds}
                            disabled={bulkPublishing || saving}
                            isPublished
                            onChange={setBulkAssignedUseCaseIds}
                          />
                          <div className={styles.actionsRow}>
                            <button
                              type="button"
                              className="btn btn-primary"
                              disabled={bulkPublishing || saving}
                              onClick={() => void handleBulkPublishTools(true)}
                            >
                              {bulkPublishing
                                ? t('loading', { ns: 'common' })
                                : t('publishSelected', { count: selectedConnectionToolIds.length })}
                            </button>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              disabled={bulkPublishing || saving}
                              onClick={() => void handleBulkPublishTools(false)}
                            >
                              {t('unpublishSelected', { count: selectedConnectionToolIds.length })}
                            </button>
                          </div>
                        </div>
                      ) : null}

                      <div className={styles.toolList}>
                        {connectionTools.map((tool) => {
                          const memberPluginNames = pluginNamesByFunctionId.get(tool.functionId) ?? [];

                          return (
                            <div key={tool.toolId} className={styles.toolListItem}>
                              {canManage ? (
                                <input
                                  type="checkbox"
                                  className={styles.toolListCheckbox}
                                  checked={selectedConnectionToolIds.includes(tool.toolId)}
                                  disabled={bulkPublishing || saving}
                                  onChange={() => toggleConnectionToolSelection(tool.toolId)}
                                  onClick={(event) => event.stopPropagation()}
                                  aria-label={tool.displayName}
                                />
                              ) : null}
                              <button
                                type="button"
                                className={[
                                  'btn',
                                  'btn-secondary',
                                  styles.toolItem,
                                  tool.isPublished ? styles.toolItemPublished : styles.toolItemDraft,
                                  selectedToolId === tool.toolId ? styles.toolItemActive : '',
                                ].filter(Boolean).join(' ')}
                                onClick={() => loadToolIntoForm(tool)}
                              >
                                <div className={styles.toolItemHeader}>
                                  <strong>{tool.displayName}</strong>
                                  <span
                                    className={[
                                      styles.toolStatusBadge,
                                      tool.isPublished ? styles.toolStatusPublished : styles.toolStatusDraft,
                                    ].join(' ')}
                                  >
                                    {tool.isPublished ? t('published') : t('draft')}
                                  </span>
                                </div>
                                <div className={styles.toolItemMeta}>
                                  <span className={styles.methodBadge}>{tool.httpMethod}</span>
                                  <span className={styles.toolItemPath}>{tool.pathTemplate}</span>
                                  {memberPluginNames.map((pluginName) => (
                                    <span
                                      key={pluginName}
                                      className={styles.pluginChip}
                                      title={t('membershipTooltip', {
                                        ns: 'toolPlugins',
                                        names: memberPluginNames.join(', '),
                                      })}
                                    >
                                      {pluginName}
                                    </span>
                                  ))}
                                </div>
                              </button>
                            </div>
                          );
                        })}
                        {connectionTools.length === 0 ? (
                          <p className={styles.fieldHint}>{t('noTools')}</p>
                        ) : null}
                      </div>
                      </aside>

                      <div className={styles.toolsEditor}>
                      {showManualToolForm && canManage ? (
                        <>
                          <div className={styles.toolsEditorHeader}>
                            <h4 className={styles.sectionTitle} style={{ margin: 0 }}>{t('createManualTool')}</h4>
                            <button
                              type="button"
                              className="btn btn-secondary"
                              onClick={closeManualToolForm}
                            >
                              {t('cancelManualTool')}
                            </button>
                          </div>
                          <ManualToolCreatePanel
                            form={manualToolForm}
                            saving={saving}
                            connectionAuthReady={connectionAuthReady}
                            onChange={setManualToolForm}
                            onSubmit={() => void handleCreateManualTool()}
                            onScrollToAuth={scrollToConnectionAuth}
                          />
                        </>
                      ) : selectedToolId ? (
                        <>
                          <h4 className={styles.sectionTitle}>{t('editTool')}</h4>
                          <div className={`company-form-grid ${styles.toolEditFormGrid}`}>
                            <FormField label={t('toolId')}>
                              <input
                                className={`text-input ${styles.readOnlyField} ${styles.monoField}`}
                                value={toolForm.toolId}
                                disabled
                                readOnly
                              />
                            </FormField>
                            <FormField label={t('functionId')}>
                              <input
                                className={`text-input ${styles.readOnlyField} ${styles.monoField}`}
                                value={toolForm.functionId}
                                disabled
                                readOnly
                              />
                            </FormField>
                            <FormField id="tool-display-name" label={t('displayName')}>
                              <input
                                id="tool-display-name"
                                className="text-input"
                                value={toolForm.displayName}
                                disabled={!canManage || saving}
                                onChange={(event) => setToolForm((current) => ({
                                  ...current,
                                  displayName: event.target.value,
                                }))}
                                placeholder={t('displayNamePlaceholder')}
                              />
                            </FormField>
                            <FormField
                              id="tool-kernel-name"
                              label={t('kernelFunctionName')}
                              hint={t('kernelFunctionNameHint')}
                            >
                              <input
                                id="tool-kernel-name"
                                className="text-input"
                                value={toolForm.kernelFunctionName}
                                disabled={!canManage || saving}
                                onChange={(event) => setToolForm((current) => ({
                                  ...current,
                                  kernelFunctionName: event.target.value,
                                }))}
                                placeholder={t('kernelFunctionNamePlaceholder')}
                                autoComplete="off"
                                spellCheck={false}
                              />
                            </FormField>
                            <FormField label={t('httpMethod')}>
                              {isManualTool ? (
                                <select
                                  className="text-input"
                                  value={toolForm.httpMethod}
                                  disabled={!canManage || saving}
                                  onChange={(event) => setToolForm((current) => ({
                                    ...current,
                                    httpMethod: event.target.value,
                                  }))}
                                >
                                  {['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'HEAD', 'OPTIONS'].map((method) => (
                                    <option key={method} value={method}>{method}</option>
                                  ))}
                                </select>
                              ) : (
                                <input
                                  className={`text-input ${styles.readOnlyField}`}
                                  value={toolForm.httpMethod}
                                  disabled
                                  readOnly
                                />
                              )}
                            </FormField>
                            <FormField label={t('pathTemplate')}>
                              <input
                                className={`text-input ${isManualTool ? '' : styles.readOnlyField} ${styles.monoField}`}
                                value={toolForm.pathTemplate}
                                disabled={!isManualTool || !canManage || saving}
                                readOnly={!isManualTool}
                                onChange={(event) => setToolForm((current) => ({
                                  ...current,
                                  pathTemplate: event.target.value,
                                }))}
                              />
                            </FormField>
                            <FormField id="tool-description" label={t('description')} fullWidth>
                              <textarea
                                id="tool-description"
                                className="text-input"
                                rows={3}
                                value={toolForm.description}
                                disabled={!canManage || saving}
                                onChange={(event) => setToolForm((current) => ({
                                  ...current,
                                  description: event.target.value,
                                }))}
                                placeholder={t('descriptionPlaceholder')}
                              />
                            </FormField>

                            <FormField id="tool-request-content-type" label={t('requestContentType')} hint={t('requestContentTypeHint')}>
                              <select
                                id="tool-request-content-type"
                                className="text-input"
                                value={toolForm.requestContentType}
                                disabled={!canManage || saving}
                                onChange={(event) => setToolForm((current) => ({
                                  ...current,
                                  requestContentType: event.target.value,
                                }))}
                              >
                                <option value="application/json">{t('requestContentTypeJson')}</option>
                                <option value="application/x-www-form-urlencoded">{t('requestContentTypeForm')}</option>
                              </select>
                            </FormField>
                            <FormField id="tool-response-mode" label={t('responseMode')}>
                              <select
                                id="tool-response-mode"
                                className="text-input"
                                value={toolForm.responseMode}
                                disabled={!canManage || saving}
                                onChange={(event) => setToolForm((current) => ({
                                  ...current,
                                  responseMode: event.target.value,
                                }))}
                              >
                                <option value="full">{t('responseModeFull')}</option>
                                <option value="jsonPath">{t('responseModeJsonPath')}</option>
                              </select>
                            </FormField>
                            {toolForm.responseMode === 'jsonPath' ? (
                              <FormField
                                id="tool-response-json-path"
                                label={t('responseJsonPath')}
                                hint={t('responseJsonPathHint')}
                                fullWidth
                              >
                                <input
                                  id="tool-response-json-path"
                                  className="text-input"
                                  value={toolForm.responseJsonPath}
                                  disabled={!canManage || saving}
                                  onChange={(event) => setToolForm((current) => ({
                                    ...current,
                                    responseJsonPath: event.target.value,
                                  }))}
                                  placeholder="data.items"
                                />
                              </FormField>
                            ) : null}

                            <label className={`${styles.publishedToggle} ${styles.formFieldFull} ${toolForm.isPublished ? styles.publishedTogglePublished : ''}`}>
                              <input
                                type="checkbox"
                                checked={toolForm.isPublished}
                                disabled={!canManage || saving}
                                onChange={(event) => setToolForm((current) => ({
                                  ...current,
                                  isPublished: event.target.checked,
                                }))}
                              />
                              <span>
                                <strong>{toolForm.isPublished ? t('published') : t('draft')}</strong>
                                <div style={{ fontSize: 12, color: '#64748b' }}>{t('publishedHelp')}</div>
                              </span>
                            </label>

                            {toolForm.isPublished && !connectionAuthReady ? (
                              <div className={`${styles.authPreviewAlert} ${styles.formFieldFull}`} role="alert">
                                <strong>{t('publishAuthRequiredTitle')}</strong>
                                <p>{t('publishAuthRequiredBody')}</p>
                                {canManage ? (
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={scrollToConnectionAuth}
                                  >
                                    {t('authPreviewUpdateCredentials')}
                                  </button>
                                ) : null}
                              </div>
                            ) : null}

                            <div className={styles.formFieldFull}>
                              <ToolUseCaseAssignment
                                selectedUseCaseIds={toolForm.assignedUseCaseIds}
                                disabled={!canManage || saving}
                                isPublished={toolForm.isPublished}
                                onChange={(assignedUseCaseIds) => setToolForm((current) => ({
                                  ...current,
                                  assignedUseCaseIds,
                                }))}
                              />
                            </div>

                            {isManualTool ? (
                              <ParameterBindingsEditor
                                bindings={toolForm.parameterBindings}
                                disabled={!canManage || saving}
                                onChange={(parameterBindings) => setToolForm((current) => ({
                                  ...current,
                                  parameterBindings,
                                }))}
                              />
                            ) : (
                              <div className={styles.formFieldFull}>
                                <strong>{t('parameterBindings')}</strong>
                                {toolForm.parameterBindings.length > 0 ? (
                                  <table className={styles.parameterTable}>
                                    <thead>
                                      <tr>
                                        <th>{t('paramName')}</th>
                                        <th>{t('paramLocation')}</th>
                                        <th>{t('paramRequired')}</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {toolForm.parameterBindings.map((binding) => (
                                        <tr key={binding.name}>
                                          <td>{binding.name}</td>
                                          <td>{binding.location}</td>
                                          <td>{binding.isRequired ? '✓' : ''}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                ) : (
                                  <div style={{ marginTop: 8, color: '#64748b' }}>{t('noParameters')}</div>
                                )}
                              </div>
                            )}

                            <div className={`${styles.sectionBlock} ${styles.formFieldFull}`}>
                              <h4 className={styles.sectionTitle}>{t('previewTool')}</h4>
                              {toolForm.parameterBindings.length > 0 ? (
                                <div className="company-form-grid">
                                  {toolForm.parameterBindings.map((binding) => (
                                    <FormField
                                      key={binding.name}
                                      id={`preview-arg-${binding.name}`}
                                      label={(
                                        <>
                                          {binding.name}
                                          {binding.isRequired ? <span style={{ color: '#dc2626' }}> *</span> : null}
                                          <span style={{ color: '#64748b', fontWeight: 400 }}> ({binding.location})</span>
                                        </>
                                      )}
                                    >
                                      <input
                                        id={`preview-arg-${binding.name}`}
                                        className="text-input"
                                        value={previewArgs[binding.name] ?? ''}
                                        disabled={!canManage || previewingTool}
                                        placeholder={t('previewArgumentPlaceholder', { name: binding.name })}
                                        onChange={(event) => setPreviewArgs((current) => ({
                                          ...current,
                                          [binding.name]: event.target.value,
                                        }))}
                                        autoComplete="off"
                                      />
                                    </FormField>
                                  ))}
                                </div>
                              ) : (
                                <p style={{ color: '#64748b', fontSize: 13 }}>{t('noParameters')}</p>
                              )}

                              {canManage ? (
                                <div className={styles.actionsRow}>
                                  <button
                                    type="button"
                                    className="btn btn-secondary"
                                    disabled={previewingTool}
                                    onClick={() => void handlePreviewTool()}
                                  >
                                    {previewingTool ? t('loading', { ns: 'common' }) : t('runPreview')}
                                  </button>
                                </div>
                              ) : null}

                              {toolPreviewAuthWarning ? (
                                <div className={styles.authPreviewAlert} role="alert">
                                  <strong>
                                    {t('authPreviewWarningTitle', {
                                      statusCode: toolPreviewAuthWarning.statusCode,
                                    })}
                                  </strong>
                                  <p>{t(toolPreviewAuthWarning.messageKey)}</p>
                                  {canManage ? (
                                    <button
                                      type="button"
                                      className="btn btn-secondary"
                                      onClick={scrollToConnectionAuth}
                                    >
                                      {t('authPreviewUpdateCredentials')}
                                    </button>
                                  ) : null}
                                </div>
                              ) : null}

                              {toolPreviewResult ? (
                                <pre className={styles.previewResult}>
                                  {toolPreviewResult}
                                </pre>
                              ) : null}
                            </div>

                            {canManage ? (
                              <div className={`${styles.actionsRow} ${styles.toolEditActions} ${styles.formFieldFull}`}>
                                <button
                                  type="button"
                                  className="btn btn-primary"
                                  disabled={saving}
                                  onClick={() => void handleSaveTool()}
                                >
                                  {t('saveTool')}
                                </button>
                                <button
                                  type="button"
                                  className="btn btn-secondary"
                                  disabled={saving}
                                  onClick={() => void handleDeleteTool()}
                                >
                                  {t('deleteTool')}
                                </button>
                              </div>
                            ) : null}
                          </div>
                        </>
                      ) : (
                        <div className={styles.toolsEditorEmpty}>
                          <strong>{t('selectToolTitle')}</strong>
                          <p>{t('selectToolHint')}</p>
                          {canManage ? (
                            <button
                              type="button"
                              className="btn btn-primary"
                              onClick={openManualToolForm}
                            >
                              {t('createManualToolShort')}
                            </button>
                          ) : null}
                        </div>
                      )}
                      </div>
                    </div>
                  </div>
                ) : null}
              </>
            ) : (
              <div style={{ color: '#64748b' }}>
                <h3>{t('emptyStateTitle')}</h3>
                <p>{t('emptyStateBody')}</p>
              </div>
            )}
          </section>
        </div>
      )}
    </>
  );

  if (embedded) {
    return pageContent;
  }

  return <div className="company-admin-page">{pageContent}</div>;
};
